import React, { useEffect, useState } from 'react';
import { FolderIcon, FolderOpenIcon, FileIcon, XIcon, PlayIcon, CheckSquare, Square, CoinsIcon, ClockIcon, AudioLinesIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { copy } from '../copy';
type Phase = 'idle' | 'scanning' | 'done';
const scanFiles = ['Production_001/Police_Reports/report_4421.pdf', 'Production_001/Body_Cam/BWC_Miller_0412.mp4', 'Production_001/Audio_911/Dispatch_2231.mp3', 'Production_002/Surveillance/Lot_C.mp4', 'Production_002/Interviews/client_0520.txt'];
export function IngestPage() {
  const navigate = useNavigate();
  const c = copy.ingest;
  const [recursive, setRecursive] = useState(true);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState('');
  const allEstimateIds = c.estimateGroups.flatMap(g => g.files.map(f => f.id));
  const [selected, setSelected] = useState<string[]>(allEstimateIds);
  useEffect(() => {
    if (phase !== 'scanning') return;
    let pct = 0;
    const interval = setInterval(() => {
      pct += 4;
      setProgress(Math.min(pct, 100));
      setCurrentFile(scanFiles[Math.floor(pct / 100 * scanFiles.length) % scanFiles.length]);
      if (pct >= 100) {
        clearInterval(interval);
        setPhase('done');
      }
    }, 120);
    return () => clearInterval(interval);
  }, [phase]);
  const startScan = () => {
    setProgress(0);
    setPhase('scanning');
  };
  const cancelScan = () => {
    setPhase('idle');
    setProgress(0);
  };
  const toggle = (id: string) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const allSelected = selected.length === allEstimateIds.length;
  return <div className="min-h-screen bg-white dark:bg-[#1a1a1a] text-slate-900 dark:text-[#e5e5e5] transition-colors duration-200" data-id="element-364">
      <Header title={c.headerTitle} minimal showBackArrow backTo="/case" data-id="element-365" />

      <main className="mx-auto max-w-4xl px-6 py-12 space-y-8" data-id="element-366">
        <div data-id="element-367">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white" data-id="element-368">
            {c.title}
          </h1>
          <p className="text-slate-500 dark:text-[#a3a3a3] mt-1" data-id="element-369">
            {c.subtitle}
          </p>
        </div>

        {/* Folder picker */}
        <section className="rounded-3xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-6 space-y-4" data-id="element-370">
          <div className="flex items-center justify-between" data-id="element-371">
            <div data-id="element-372">
              <h2 className="font-semibold text-slate-900 dark:text-white" data-id="element-373">
                {c.pickerTitle}
              </h2>
              <p className="text-sm text-slate-500 dark:text-[#a3a3a3]" data-id="element-374">
                {c.pickerHint}
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-[#a3a3a3] cursor-pointer select-none" data-id="element-375">
              <input type="checkbox" checked={recursive} onChange={e => setRecursive(e.target.checked)} className="h-4 w-4 rounded border-slate-300 dark:border-[#444] accent-blue-500" data-id="element-376" />
              {c.recursiveLabel}
            </label>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] p-4" data-id="element-377">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-[#555] mb-3" data-id="element-378">
              {c.treePreviewTitle}
            </div>
            <ul className="space-y-1" data-id="element-379">
              {c.tree.map(root => <li key={root.name} data-id="element-380">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-[#e5e5e5]" data-id="element-381">
                    <FolderOpenIcon size={15} className="text-blue-500" data-id="element-382" />
                    {root.name}
                  </div>
                  {recursive && <ul className="ml-5 mt-1 space-y-1 border-l border-slate-100 dark:border-[#262626] pl-3" data-id="element-383">
                      {root.children.map(child => <li key={child.name} className="flex items-center justify-between text-sm text-slate-500 dark:text-[#a3a3a3]" data-id="element-384">
                          <span className="flex items-center gap-2" data-id="element-385">
                            <FolderIcon size={14} className="text-slate-400" data-id="element-386" />
                            {child.name}
                          </span>
                          <span className="text-xs text-slate-400 dark:text-[#555]" data-id="element-387">
                            {child.count} files
                          </span>
                        </li>)}
                    </ul>}
                </li>)}
            </ul>
          </div>

          <p className="text-xs text-slate-400 dark:text-[#555] leading-relaxed" data-id="element-388">
            {c.cheapPassNote}
          </p>

          {phase === 'idle' && <button onClick={startScan} className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-medium hover:bg-slate-800 dark:hover:bg-slate-100 transition-all shadow-sm" data-id="element-389">
              <PlayIcon size={16} data-id="element-390" />
              {c.scanButton}
            </button>}

          {phase === 'scanning' && <div className="space-y-3" data-id="element-391">
              <div className="flex items-center justify-between text-sm" data-id="element-392">
                <span className="flex items-center gap-2 text-slate-600 dark:text-[#a3a3a3]" data-id="element-393">
                  <FileIcon size={14} className="text-blue-500" data-id="element-394" />
                  <span className="font-mono text-xs truncate max-w-[280px]" data-id="element-395">
                    {currentFile}
                  </span>
                </span>
                <button onClick={cancelScan} className="flex items-center gap-1 text-sm text-slate-500 dark:text-[#a3a3a3] hover:text-red-500" data-id="element-396">
                  <XIcon size={14} data-id="element-397" />
                  {c.cancelLabel}
                </button>
              </div>
              <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-[#333] overflow-hidden" data-id="element-398">
                <motion.div className="h-full rounded-full bg-blue-500" animate={{
              width: `${progress}%`
            }} transition={{
              ease: 'linear',
              duration: 0.12
            }} data-id="element-399" />
              </div>
              <div className="text-xs text-slate-400 dark:text-[#555]" data-id="element-400">
                {c.scanningLabel} — {progress}%
              </div>
            </div>}
        </section>

        {/* Transcription cost estimate gate */}
        <AnimatePresence data-id="element-401">
          {phase === 'done' && <motion.section initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} className="rounded-3xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] p-6 space-y-5" data-id="element-402">
              <div data-id="element-403">
                <h2 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2" data-id="element-404">
                  <CoinsIcon size={18} className="text-amber-500" data-id="element-405" />
                  {c.estimateTitle}
                </h2>
                <p className="text-sm text-slate-500 dark:text-[#a3a3a3] mt-1" data-id="element-406">
                  {c.estimateSubtitle}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-407">
                <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-4" data-id="element-408">
                  <div className="flex items-center gap-2 text-slate-400 dark:text-[#555] text-xs mb-1" data-id="element-409">
                    <AudioLinesIcon size={13} data-id="element-410" />
                    Spoken-audio files
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white" data-id="element-411">
                    {c.estimate.spokenAudioCount}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-4" data-id="element-412">
                  <div className="flex items-center gap-2 text-slate-400 dark:text-[#555] text-xs mb-1" data-id="element-413">
                    <ClockIcon size={13} data-id="element-414" />
                    Total media duration
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white" data-id="element-415">
                    {c.estimate.totalDuration}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-4" data-id="element-416">
                  <div className="flex items-center gap-2 text-slate-400 dark:text-[#555] text-xs mb-1" data-id="element-417">
                    <CoinsIcon size={13} data-id="element-418" />
                    Estimated cost
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white" data-id="element-419">
                    {c.estimate.costLow}–{c.estimate.costHigh}
                  </div>
                  <div className="text-[10px] text-slate-400 dark:text-[#555] mt-0.5" data-id="element-420">
                    {c.estimate.tokenRangeLow}–{c.estimate.tokenRangeHigh}{' '}
                    tokens
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 dark:border-[#333] overflow-hidden" data-id="element-421">
                <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 dark:bg-[#222] border-b border-slate-200 dark:border-[#333]" data-id="element-422">
                  <button onClick={() => setSelected(allSelected ? [] : allEstimateIds)} className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-[#a3a3a3]" data-id="element-423">
                    {allSelected ? <CheckSquare size={16} className="text-blue-500" data-id="element-424" /> : <Square size={16} data-id="element-425" />}
                    {c.selectAllLabel}
                  </button>
                </div>
                {c.estimateGroups.map(group => <div key={group.subfolder} data-id="element-426">
                    <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-[#555] bg-slate-50/50 dark:bg-[#222]/50" data-id="element-427">
                      {group.subfolder}
                    </div>
                    {group.files.map(file => <label key={file.id} className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 dark:border-[#262626] cursor-pointer hover:bg-slate-50 dark:hover:bg-[#222]" data-id="element-428">
                        <span className="flex items-center gap-3 text-sm text-slate-700 dark:text-[#e5e5e5]" data-id="element-429">
                          {selected.includes(file.id) ? <CheckSquare size={16} className="text-blue-500" data-id="element-430" /> : <Square size={16} className="text-slate-400 dark:text-[#555]" data-id="element-431" />}
                          <input type="checkbox" className="sr-only" checked={selected.includes(file.id)} onChange={() => toggle(file.id)} data-id="element-432" />
                          {file.name}
                        </span>
                        <span className="text-xs text-slate-400 dark:text-[#555]" data-id="element-433">
                          {file.duration}
                        </span>
                      </label>)}
                  </div>)}
              </div>

              <div className="flex items-center justify-end gap-3" data-id="element-434">
                <span className="text-sm text-slate-500 dark:text-[#a3a3a3]" data-id="element-435">
                  {selected.length} selected
                </span>
                <button disabled={selected.length === 0} onClick={() => navigate('/case/files')} className="px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-40 transition-all shadow-sm" data-id="element-436">
                  {c.confirmButton}
                </button>
              </div>
            </motion.section>}
        </AnimatePresence>
      </main>
    </div>;
}